import React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function FilterBar({ filters, setFilters, categories, ventures, states, platforms }) {
  const date = filters.date ? new Date(filters.date) : null;

  return (
    <div className="flex flex-wrap items-end gap-3 p-4 border-b border-slate-200 bg-white" data-testid="filter-bar">
      <FilterSelect
        label="Business"
        value={filters.venture || "all"}
        onChange={(v) => setFilters({ ...filters, venture: v === "all" ? "" : v })}
        options={ventures}
        testId="filter-venture"
      />
      <FilterSelect
        label="Category"
        value={filters.category || "all"}
        onChange={(v) => setFilters({ ...filters, category: v === "all" ? "" : v })}
        options={categories}
        testId="filter-category"
      />
      <FilterSelect
        label="Severity"
        value={filters.severity || "all"}
        onChange={(v) => setFilters({ ...filters, severity: v === "all" ? "" : v })}
        options={["critical", "high", "medium", "low"]}
        testId="filter-severity"
      />
      <FilterSelect
        label="State"
        value={filters.state || "all"}
        onChange={(v) => setFilters({ ...filters, state: v === "all" ? "" : v })}
        options={states}
        testId="filter-state"
      />
      <FilterSelect
        label="Platform"
        value={filters.platform || "all"}
        onChange={(v) => setFilters({ ...filters, platform: v === "all" ? "" : v })}
        options={platforms}
        testId="filter-platform"
      />
      <div className="min-w-[180px]">
        <div className="kicker mb-1.5">Analysis Date</div>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "h-9 w-full justify-start text-left font-normal rounded-sm border-slate-300 px-3 text-sm",
                !date && "text-muted-foreground"
              )}
              data-testid="filter-date-trigger"
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {date ? format(date, "PPP") : <span>Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 rounded-sm" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => {
                const val = d ? format(d, "yyyy-MM-dd") : "";
                setFilters({ ...filters, date: val });
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
      <button
        className="text-[11px] uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 border border-slate-300 h-9 px-3 rounded-sm"
        onClick={() => setFilters({})}
        data-testid="clear-filters"
      >
        Clear all
      </button>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  const plural = label === "Category" ? "categories" : label === "Severity" ? "severities" : label === "Business" ? "businesses" : `${label.toLowerCase()}s`;
  return (
    <div className="min-w-[160px]">
      <div className="kicker mb-1.5">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 rounded-sm border-slate-300 text-sm" data-testid={testId}>
          <SelectValue placeholder={`All ${plural}`} />
        </SelectTrigger>
        <SelectContent className="rounded-sm">
          <SelectItem value="all">All {plural}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o} value={o}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
